/**
 * 统一取消任务：调后端 API 删除 + 清 Yjs 节点数据 + 清 taskStore。
 *
 * 节点上的取消按钮和任务列表面板的取消按钮都使用此函数，
 * 确保两端处理流程一致。
 */
import { readNodeCustomDataSnapshot } from '@/entities/node';
import { getProjectId } from '@/entities/project';
import type { TaskPanelEntry } from '@/features/task-list/model/task-types';
import { yNodesMetadata, yTaskInfo } from '@/shared/config/yjs-config';
import SLog from '@/shared/lib/SLog';
import { clearTaskInfoIfCurrentRun, type GlobalTaskInfo } from '@/shared/lib/task/task-info-store';
import { cancelTask as clearYjsTask } from '@/shared/lib/task/task-provider';
import { toast } from 'sonner';
import { requestTaskCancel as requestTaskCancelApi, requestTaskDelete } from '../api/task-api';
import { clearTaskState } from './task-store';

export interface TaskCancelRequestItem {
    runId: string
    nodeId?: string
    taskId?: string
    scope?: 'node' | 'global'
}

export async function cancelTaskFromTaskPanel(isDelete: boolean, item: TaskPanelEntry): Promise<boolean> {
    console.log('cancelTaskFromTaskPanel', isDelete)
    if(isDelete) {
        const res = await requestTaskDelete(item.taskId)
        if(res.code != 1) {
            toast.error(res.msg)
            return false;
        }
        // 5. 清 taskStore 状态
        clearTaskState({ taskId: item.taskId })
        SLog.logEvent('task_del', `project_id-${getProjectId()} taskId-${item.taskId}`);
        return true;
    }
    const taskInfo = yTaskInfo.toJSON() as GlobalTaskInfo
    if (item.nodeId && taskInfo.state == 0 && taskInfo.runId && taskInfo.runMap) {
        const list = Object.entries(taskInfo.runMap).filter(item => item[1] == 0).map(item => item[0].split('#')[0]);
        if(list.indexOf(item.nodeId) != -1) {
            return await cancelStreamTask();
        }
    }
    if(!item.runId) {
        toast.error('runId 参数缺失')
        return false;
    }
    // 没生成完走取消任务
    const res = await requestTaskCancelApi(item.runId)
    if(res.code != 1) {
        toast.error(res.msg)
        return false;
    }
    const data = item.nodeId ? readNodeCustomDataSnapshot(item.nodeId) : null;
    SLog.logEvent('task_cancel', `project_id-${getProjectId()} node_id-${item.nodeId} node_type-${item.nodeType} op-${data?.op} run_id-${item.runId}`);
    if(item.nodeId) {
        // 4. 清 Yjs 节点数据（status / runId）
        clearYjsTask(item.nodeId)
    }
    // 5. 清 taskStore 状态
    clearTaskState({ taskId: item.taskId })
    return true;
}

/**
 * 统一取消任务。
 * @param nodeId  画布节点 ID（必填，用于清除 Yjs 数据）
 * @param taskId  服务端任务 ID（可选，不传则从 taskStore 查找）
 */
export async function cancelNodeTask(nodeId: string, taskId?: string): Promise<boolean> {
    const data = readNodeCustomDataSnapshot(nodeId);
    const runId = data.runId;
    taskId = taskId || data.taskId;
    const isComplete = data.status == 'completed' || data.status == 'failed' || data.status == 'canceled';
    if (isComplete && !!taskId) {
        // 没生成完走删除任务
        const res = await requestTaskDelete(taskId)
        if(res.code != 1) {
            toast.error(res.msg)
            return false;
        }
    } else if (!isComplete && !!runId) {
        // 生成中走取消任务
        const res = await requestTaskCancelApi(runId)
        if(res.code != 1) {
            toast.error(res.msg)
            return false;
        }
    }

    const nodeType = (yNodesMetadata.get(nodeId) as any)?.get('type');
    SLog.logEvent('task_cancel', `project_id-${getProjectId()} node_id-${nodeId} node_type-${nodeType} op-${data.op} run_id-${data.runId}`);

    // 4. 清 Yjs 节点数据（status / runId）
    clearYjsTask(nodeId)

    // 5. 清 taskStore 状态
    clearTaskState({ taskId })
    return true;
}

export async function requestTaskCancel(items: TaskCancelRequestItem[]): Promise<boolean> {
    const normalizedItems = items
        .map((item) => ({ ...item, runId: item.runId.trim() }))
        .filter((item) => item.runId)

    if (normalizedItems.length === 0) {
        return true
    }

    const uniqueRunIds = Array.from(new Set(normalizedItems.map((item) => item.runId)))
    const results = await Promise.allSettled(
        uniqueRunIds.map(async (runId) => {
            const res = await requestTaskCancelApi(runId)
            if (res.code != 1) {
                throw new Error(res.msg || '取消任务失败')
            }
        }),
    )

    const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
    if (failed) {
        const reason = failed.reason
        toast.error(reason instanceof Error && reason.message ? reason.message : '取消任务失败')
        return false
    }

    normalizedItems.forEach((item) => {
        if (item.scope === 'global') {
            return
        }

        if (item.nodeId) {
            clearYjsTask(item.nodeId)
        }

        if (item.taskId) {
            clearTaskState({ taskId: item.taskId })
        } else if (item.nodeId) {
            clearTaskState({ nodeId: item.nodeId })
        }

        const nodeData = (item.nodeId ? yNodesMetadata.get(item.nodeId) : null) as any;
        SLog.logEvent('task_cancel', `project_id-${getProjectId()} node_id-${item.nodeId} node_type-${nodeData?.get('type') || ''} op-${nodeData?.get('op') ||''} run_id-${item.runId}`);
    })

    if (normalizedItems.some((item) => item.scope === 'global')) {
        await clearTaskInfoIfCurrentRun()
    }

    return true
}

export async function cancelStreamTask() {
    const taskInfo = yTaskInfo.toJSON() as GlobalTaskInfo
    if (taskInfo.state != 0 || !taskInfo.runId) return false;
    const res = await requestTaskCancelApi(taskInfo.runId)
    if(res.code != 1) {
        toast.error(res.msg)
        return false;
    }
    await clearTaskInfoIfCurrentRun();
    SLog.logEvent('task_cancel_group', `project_id-${getProjectId()} run_id-${taskInfo.runId} group-${taskInfo.isGroup ? 1 : 0} node_id-${taskInfo.nodeId}`);
    return true;
}

